import {
  useQuery,
  useInfiniteQuery,
  type UseQueryResult,
  type UseInfiniteQueryResult,
  type UseQueryOptions,
  type UseInfiniteQueryOptions,
  type QueryKey,
  type InfiniteData,
} from '@tanstack/react-query';

/**
 * Обертка над useQuery с автоматической проверкой авторизации пользователя.
 * Запрос не выполняется если userId пустой (enabled: !!userId).
 *
 * @param userId - ID авторизованного пользователя
 * @param options - Стандартные опции useQuery
 * @returns Результат useQuery
 */
export function useAuthQuery<
  TQueryFnData = unknown,
  TError = Error,
  TData = TQueryFnData,
  TQueryKey extends QueryKey = QueryKey,
>(
  userId: string | null | undefined,
  options: Omit<
    UseQueryOptions<TQueryFnData, TError, TData, TQueryKey>,
    'enabled'
  > & {
    enabled?: boolean;
  },
): UseQueryResult<TData, TError> {
  return useQuery({
    ...options,
    // Комбинируем проверку userId с дополнительными условиями enabled из options
    enabled: !!userId && (options.enabled ?? true),
  });
}

/**
 * Обертка над useInfiniteQuery с автоматической проверкой авторизации пользователя.
 * Запрос не выполняется если userId пустой (enabled: !!userId).
 *
 * @param userId - ID авторизованного пользователя
 * @param options - Стандартные опции useInfiniteQuery
 * @returns Результат useInfiniteQuery
 */
export function useAuthInfiniteQuery<
  TQueryFnData = unknown,
  TError = Error,
  TData = InfiniteData<TQueryFnData>,
  TQueryKey extends QueryKey = QueryKey,
  TPageParam = unknown,
>(
  userId: string | null | undefined,
  options: Omit<
    UseInfiniteQueryOptions<
      TQueryFnData,
      TError,
      TData,
      TQueryKey,
      TPageParam
    >,
    'enabled'
  > & {
    enabled?: boolean;
  },
): UseInfiniteQueryResult<TData, TError> {
  return useInfiniteQuery({
    ...options,
    // Комбинируем проверку userId с дополнительными условиями enabled из options
    enabled: !!userId && (options.enabled ?? true),
  });
}